import { Button, CustomSelect, FileUpload, TextInput, type SelectOption } from '@consigli/facade';
import { Language } from '@consigli/types';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineTrash } from 'react-icons/hi';

import { Bid } from './types';

const MAX_FILE_SIZE_BYTES = 600 * 1024 * 1024;

interface BidCardProps {
  bid: Bid;
  index: number;
  canRemove: boolean;
  onChange: (bid: Bid) => void;
  onRemove: () => void;
}

export const BidCard: FC<BidCardProps> = ({ bid, index, canRemove, onChange, onRemove }) => {
  const { t } = useTranslation();
  const [showMessagePanel, setShowMessagePanel] = useState(true);

  const languageOptions: SelectOption[] = useMemo(
    () => [
      { label: t('language.en'), value: Language.EN },
      { label: t('language.fr'), value: Language.FR },
      { label: t('language.de'), value: Language.DE },
      { label: t('language.ja'), value: Language.JA },
      { label: t('language.nb'), value: Language.NB },
      { label: t('language.nn'), value: Language.NN },
      { label: t('language.es'), value: Language.ES },
      { label: t('language.ca'), value: Language.CA },
      { label: t('language.sv'), value: Language.SV },
      { label: t('language.vi'), value: Language.VI },
      { label: t('language.zh'), value: Language.ZH },
    ],
    [t],
  );

  const setFiles: Dispatch<SetStateAction<File[]>> = (value) => {
    const nextFiles = typeof value === 'function' ? value(bid.files) : value;
    onChange({ ...bid, files: nextFiles });
  };

  return (
    <div className="w-full rounded-lg border border-day-light-2 bg-day-light-5 px-6 py-5">
      <div className="mb-4 flex items-center justify-between text-day-neutral-subtle">
        <span className="text-lg font-semibold text-day-neutral-dark">
          {t('bid-review.bid-details.bid-label', { number: index + 1 })}
        </span>
        {canRemove && (
          <Button
            ghost
            icon={HiOutlineTrash}
            iconSize={16}
            onClick={onRemove}
            className="gap-1 text-sm"
          >
            {t('bid-review.bid-details.remove-bid')}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-day-neutral-dark">
            {t('bid-review.bid-details.provider-name')}
          </label>
          <TextInput
            value={bid.providerName}
            onChange={(event) => onChange({ ...bid, providerName: event.target.value })}
            placeholder={t('bid-review.bid-details.provider-name-placeholder')}
          />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-day-neutral-dark">
            {t('bid-review.bid-details.language')}
          </label>
          <CustomSelect
            options={languageOptions}
            placeholder={t('language.select')}
            noOptionsMessage={t('custom-select.no-options')}
            value={languageOptions.filter((option) => option.value === bid.language)}
            onChange={(selectedOptions: SelectOption[]) =>
              onChange({
                ...bid,
                language: selectedOptions.length > 0 ? String(selectedOptions[0].value) : '',
              })
            }
          />
        </div>
      </div>

      <div className="mt-4">
        <span className="block text-sm font-medium text-day-neutral-dark">
          {t('bid-review.bid-details.documents')}
        </span>
        <span className="block text-xs text-day-neutral-subtle">
          {t('bid-review.bid-details.documents-hint')}
        </span>
        <FileUpload
          files={bid.files}
          setFiles={setFiles}
          dropAreaLabel={t('document-upload.drag-n-drop-here')}
          dropAreaClassName="mt-2 h-40 w-full"
          fileButtonLabel={t('document-upload.browse-files')}
          folderButtonLabel={t('document-upload.browse-folders')}
          uploadListLabel={t('document-upload.files')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
          showMessagePanel={showMessagePanel}
          setShowMessagePanel={setShowMessagePanel}
          maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
          fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
        />
      </div>
    </div>
  );
};
